/**
 * Design tokens from DESIGN_SPEC.md. All positions are reference pixels on the
 * 1024 × 1536 portrait design surface; the renderer scales them to the device.
 * Change a value here and every screen follows.
 */

/** §2 Palette – the only colours allowed in the game. */
export const COLORS = {
  cream: 0xf7eee3,
  latte: 0xefe1cf,
  cocoa: 0x3a2620,
  wood: 0x7a4e36,
  raspberry: 0xd6457a,
  butter: 0xf6c85f,
  mint: 0xe4f1ea,
  sky: 0xcfe8f7,
  blush: 0xffd0e0,
  lavender: 0xe3d6ff,
  sage: 0xd5f0c9,
  peach: 0xffd9c2,
} as const;
export type ColorToken = keyof typeof COLORS;

/** §3 Typography (sizes in reference px). */
export const FONT = {
  family: 'Fredoka, system-ui, sans-serif',
  size: {
    celebration: 60,
    screenTitle: 46,
    primaryButton: 42,
    cardTitle: 32,
    body: 30,
    secondary: 26,
    movesCounter: 84,
  },
  weight: { heading: '700', button: '600', body: '500' },
  lineHeight: 1.15,
} as const;

/** §4 Screen surface and spacing. */
export const SCREEN = {
  width: 1024,
  height: 1536,
  safeTop: 92,
  safeBottom: 61,
  margin: 64,
  space: [8, 16, 24, 32, 48, 64] as const,
  minTouch: 44,
} as const;

/** §5 Outlines and radii. */
export const SHAPE = {
  outline: 4,
  panelRadius: 30,
  buttonRadius: 26,
  cardRadius: 24,
  panelLip: 5,
} as const;

/** §6 Board and HUD layout (reference px, measured from the top). */
export const BOARD = {
  cols: 8,
  rows: 8,
  frame: { x: 61, y: 415, w: 902, h: 902 },
  inner: { x: 80, y: 434, w: 864, h: 864 },
  cell: 108,
  /** tile silhouette ≈ 76–80 % of the cell */
  tileScale: 0.8,
} as const;

export const HUD = {
  titleY: 108,
  moves: { x: 61, y: 200, w: 277, h: 169 },
  goals: { x: 369, y: 200, w: 594, h: 169 },
  boosters: { x: 61, y: 1352, w: 686, h: 108 },
  /** cheering dog next to the booster bar, fully above y = 1475 */
  dog: { x: 860, bottom: 1470, height: 190 },
} as const;

/** §13 Animation timings in milliseconds. */
export const TIMING = {
  buttonPress: 100,
  buttonPressScale: 0.97,
  tileSelectScale: 1.08,
  swap: 160,
  matchPop: 180,
  panelIn: 250,
  starGap: 120,
  coinCount: 400,
  furniturePlace: 220,
  renovate: 600,
  arrival: 600,
  sparkle: 250,
  heart: 700,
  shockwave: 300,
  confetti: 1000,
  breathe: 3000,
} as const;
